// PDF de los registros firmados (M45) con el estilo del PDF de inspecciones (insp-pdf.ts): banda oscura, campos en
// dos columnas, tabla con un recuadro de firma por fila, pie con página. Dos modos:
//  - registro: con las firmas en pantalla (trazo, nombre, cédula, fecha y hora del servidor y SHA-256) o, si se firmó
//    en papel, la nota del escaneo adjunto;
//  - formato en blanco para firma manual: los mismos datos con los recuadros vacíos (se imprime, se firma y se sube
//    el escaneo). Lleva la huella del contenido para que el escaneo se pueda cotejar con el registro.

import { jsPDF } from 'jspdf';
import { fechaEc } from '../fechas';
import { paraPdf } from '../pe/pe-plan-pdf';
import { firmasRequeridas } from './firmas-logica';
import { conLista, type Contenido, type EstadoRegistro, type MetodoRegistro, type RolFirmante } from './firmas-tipos';
import {
  ETIQUETA_APTITUD, ETIQUETA_CLASE, ETIQUETA_EVALUACION, ETIQUETA_METODO, ETIQUETA_ROL, ETIQUETA_TIPO_EXAMEN, MARCA_TRABAJADOR_ELIMINADO,
  TEXTO_FIRMA_SIMPLE,
} from './firmas-tipos';

export interface FirmaParaPdf {
  rol: RolFirmante; worker_id: string | null; nombre: string; cedula: string | null; firmadoEn: string; hash: string; imagen: string | null;
}

export interface DatosRegistroPDF {
  contenido: Contenido;
  hash: string | null;                       // SHA-256 del contenido congelado
  estado: EstadoRegistro | 'sin_registro';
  metodo: MetodoRegistro | null;
  firmadoEn: string | null;                  // hora del servidor al cerrar el registro
  firmas: FirmaParaPdf[];
  devoluciones?: Record<string, string | null>;   // EPP: fecha de devolución por asignación
  certificadoMedico?: boolean;               // aptitud: el certificado firmado por el médico está adjunto
  reaperturas?: number;
  trabajadorEliminado?: boolean;             // entrega de EPP o aptitud: el trabajador ya no existe en Trabajadores
  blanco: boolean;                           // formato en blanco para firma manual
  fecha: Date;                               // fecha de generación
}

const MARGEN = 15;
const ANCHO = 210 - MARGEN * 2;
const ALTO_UTIL = 297 - 20;
const ARRIBA = 22;

// dd/mm/aaaa: una fecha sin hora, tal cual; un instante (signed_at), en el día de Ecuador (lib/fechas).
export const fechaCorta = (iso: string | null | undefined) => fechaEc(iso);
export const fechaHora = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString('es-EC', { timeZone: 'America/Guayaquil', dateStyle: 'short', timeStyle: 'medium' }) : '—';

export function tituloRegistro(c: Contenido): string {
  if (c.tipo === 'socializacion') return 'Registro de socialización del procedimiento';
  if (c.tipo === 'asistencia') {
    return c.clase === 'induccion' ? 'Registro de asistencia a inducción' : c.clase === 'entrenamiento'
      ? 'Registro de asistencia a entrenamiento' : 'Registro de asistencia a capacitación';
  }
  return c.tipo === 'epp_entrega' ? 'Registro de entrega-recepción de EPP' : 'Certificado de aptitud médica laboral';
}

export function nombreArchivoRegistro(c: Contenido, blanco: boolean): string {
  const base = c.tipo === 'socializacion' ? `Socializacion_${c.titulo}` : c.tipo === 'asistencia' ? `Asistencia_${c.titulo}` : c.tipo === 'epp_entrega' ? `Entrega_EPP_${c.trabajador}` : `Aptitud_${c.trabajador}`;
  const limpio = base.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 50);
  return `${limpio}_${c.fecha}${blanco ? '_para_firmar' : ''}.pdf`;
}

export function generarRegistroPDF(d: DatosRegistroPDF): jsPDF {
  const c = d.contenido;
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const T = (s: string) => paraPdf(s);
  const titulo = tituloRegistro(c);
  let y = ARRIBA;
  const nuevaPagina = () => { doc.addPage(); y = ARRIBA; };
  const espacio = (alto: number) => { if (y + alto > ALTO_UTIL) nuevaPagina(); };
  const texto = (s: string, tam = 9, o: { negrita?: boolean; gris?: boolean } = {}) => {
    doc.setFont('helvetica', o.negrita ? 'bold' : 'normal'); doc.setFontSize(tam);
    if (o.gris) doc.setTextColor(100, 116, 139); else doc.setTextColor(15, 23, 42);
    (doc.splitTextToSize(T(s), ANCHO) as string[]).forEach(l => { espacio(tam * 0.45); doc.text(l, MARGEN, y); y += tam * 0.45; });
    doc.setTextColor(15, 23, 42);
  };
  const seccion = (s: string) => {
    espacio(14); y += 3;
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11.5); doc.setTextColor(29, 111, 163);
    doc.text(T(s), MARGEN, y);
    doc.setDrawColor(203, 213, 225); doc.line(MARGEN, y + 1.5, MARGEN + ANCHO, y + 1.5);
    y += 6.5; doc.setTextColor(15, 23, 42);
  };
  const campos = (pares: [string, string][]) => {
    for (const [k, v] of pares) {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
      const valor = doc.splitTextToSize(T(v || '—'), ANCHO - 52) as string[];
      espacio(valor.length * 4.2 + 2);
      const y0 = y;
      doc.setFont('helvetica', 'bold'); doc.text(T(`${k}:`), MARGEN, y0);
      doc.setFont('helvetica', 'normal'); valor.forEach((l, n) => doc.text(l, MARGEN + 52, y0 + n * 4.2));
      y += (valor.length - 1) * 4.2 + 5.2;
    }
  };
  // Con el trabajador borrado, la firma no tiene vínculo (worker_id null) y se empareja por la cédula congelada.
  const firmaDe = (rol: RolFirmante, workerId: string | null, cedula?: string | null) =>
    d.firmas.find(f => f.rol === rol && (!workerId || f.worker_id === workerId || (f.worker_id === null && !!cedula && f.cedula === cedula)));
  const conMarca = (nombre: string, eliminado: boolean) => (eliminado ? `${nombre} ${MARCA_TRABAJADOR_ELIMINADO}` : nombre);
  const enPapel = d.metodo === 'papel';
  /** Recuadro de firma (x, y0, ancho, alto) con el trazo o vacío, y los datos debajo si se pide. */
  const recuadro = (x: number, y0: number, ancho: number, alto: number, f: FirmaParaPdf | undefined) => {
    doc.setDrawColor(148, 163, 184); doc.rect(x, y0, ancho, alto);
    if (d.blanco) return;
    if (f?.imagen) { try { doc.addImage(f.imagen, 'PNG', x + 1, y0 + 0.5, ancho - 2, alto - 1); } catch { /* imagen dañada: queda el recuadro */ } }
    else if (enPapel) { doc.setFontSize(6.5); doc.setTextColor(100, 116, 139); doc.text(T('En el formato escaneado'), x + 2, y0 + alto / 2 + 1); doc.setTextColor(15, 23, 42); }
  };
  const datosFirma = (x: number, yy: number, f: FirmaParaPdf | undefined) => {
    if (d.blanco || !f) return;
    doc.setFont('helvetica', 'normal'); doc.setFontSize(6.3); doc.setTextColor(100, 116, 139);
    doc.text(T(`${fechaHora(f.firmadoEn)} · SHA-256 ${f.hash.slice(0, 24)}…`), x, yy);
    doc.setTextColor(15, 23, 42);
  };

  // Portada
  doc.setFillColor(15, 23, 42); doc.rect(0, 0, 210, 46, 'F');
  doc.setTextColor(255, 255, 255); doc.setFont('helvetica', 'bold'); doc.setFontSize(17);
  doc.text(T(titulo), MARGEN, 22);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(11);
  doc.text(T(c.empresa || 'Empresa'), MARGEN, 32);
  doc.setFontSize(9.5);
  doc.text(T(d.blanco ? 'Formato para firma manual' : d.estado === 'firmado' ? 'Registro firmado' : 'Registro en firma (sin cerrar)'), MARGEN, 40);
  doc.setTextColor(15, 23, 42);
  y = 56;

  if (conLista(c)) {
    campos(c.tipo === 'socializacion' ? [
      ['Procedimiento', c.titulo], ['Fecha', fechaCorta(c.fecha)], ['Tema', c.tema || c.titulo], ['Socializado por', c.tecnico],
      ['Material', c.material || '—'], ['Evaluación', c.evaluacion || '—'],
      ['Duración', c.duracion_horas !== null ? `${String(c.duracion_horas).replace('.', ',')} horas` : '—'],
    ] : [
      ['Tipo', ETIQUETA_CLASE[c.clase]], ['Fecha', fechaCorta(c.fecha)], ['Título', c.titulo], ['Tema', c.tema || c.titulo],
      ['Técnico o capacitador', c.tecnico], ['Material', c.material], ['Evaluación', c.evaluacion],
      ['Duración', c.duracion_horas !== null ? `${String(c.duracion_horas).replace('.', ',')} horas` : '—'],
    ]);
    seccion('Asistentes');
    const cols = { n: 8, nombre: 62, cedula: 26, eval: 26 };
    const xFirma = MARGEN + cols.n + cols.nombre + cols.cedula + cols.eval;
    const anchoFirma = MARGEN + ANCHO - xFirma;
    espacio(8);
    doc.setFillColor(241, 245, 249); doc.rect(MARGEN, y - 4, ANCHO, 6, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8);
    doc.text('N.', MARGEN + 1, y); doc.text(T('Nombre'), MARGEN + cols.n, y); doc.text(T('Cédula'), MARGEN + cols.n + cols.nombre, y);
    doc.text(T('Evaluación'), MARGEN + cols.n + cols.nombre + cols.cedula, y); doc.text(T('Firma'), xFirma + 1, y);
    y += 4;
    const alto = 14;
    c.asistentes.forEach((a, k) => {
      espacio(alto + 4);
      const y0 = y;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5);
      doc.text(String(k + 1), MARGEN + 1, y0 + 6);
      const f = firmaDe('trabajador', a.worker_id, a.cedula);
      doc.text(doc.splitTextToSize(T(conMarca(a.nombre, !!f && f.worker_id === null)), cols.nombre - 2) as string[], MARGEN + cols.n, y0 + 6);
      doc.text(T(a.cedula || '—'), MARGEN + cols.n + cols.nombre, y0 + 6);
      const ev = a.evaluacion ? ETIQUETA_EVALUACION[a.evaluacion] + (a.nota !== null ? ` (${String(a.nota).replace('.', ',')})` : '') : '';
      doc.text(T(d.blanco && !ev ? '' : ev || '—'), MARGEN + cols.n + cols.nombre + cols.cedula, y0 + 6);
      recuadro(xFirma, y0, anchoFirma, alto - 3, f);
      datosFirma(xFirma, y0 + alto - 0.5, f);
      doc.setDrawColor(226, 232, 240); doc.line(MARGEN, y0 + alto + 1.5, MARGEN + ANCHO, y0 + alto + 1.5);
      y += alto + 3;
    });
    if (!c.asistentes.length) texto('Sin asistentes.', 8.5, { gris: true });
  } else if (c.tipo === 'epp_entrega') {
    campos([['Trabajador', conMarca(c.trabajador, !!d.trabajadorEliminado)], ['Cédula', c.cedula], ['Fecha de entrega', fechaCorta(c.fecha)]]);
    seccion('Detalle del EPP entregado');
    const cols = { epp: 58, marca: 40, cond: 24, vence: 24 };
    espacio(8);
    doc.setFillColor(241, 245, 249); doc.rect(MARGEN, y - 4, ANCHO, 6, 'F');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8);
    let x = MARGEN + 1;
    for (const [t, w] of [['EPP', cols.epp], ['Marca y modelo', cols.marca], ['Condición', cols.cond], ['Vence', cols.vence], ['Devolución', 30]] as [string, number][]) {
      doc.text(T(t), x, y); x += w;
    }
    y += 5;
    c.items.forEach(i => {
      espacio(7);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5);
      let xx = MARGEN + 1;
      const devo = d.devoluciones?.[i.asignacion_id];
      for (const [t, w] of [[i.epp, cols.epp], [[i.marca, i.modelo].filter(Boolean).join(' ') || '—', cols.marca], [i.condicion ?? '—', cols.cond],
        [fechaCorta(i.vence), cols.vence], [d.blanco ? '' : devo ? fechaCorta(devo) : 'No devuelto', 30]] as [string, number][]) {
        doc.text((doc.splitTextToSize(T(t), w - 2) as string[])[0] ?? '', xx, y); xx += w;
      }
      doc.setDrawColor(226, 232, 240); doc.line(MARGEN, y + 2, MARGEN + ANCHO, y + 2);
      y += 6.5;
    });
    if (!c.items.length) texto('Sin EPP.', 8.5, { gris: true });
    y += 2;
    texto('El trabajador declara que recibe el equipo de protección personal detallado, en las condiciones indicadas, y se compromete a usarlo, cuidarlo y devolverlo cuando corresponda.', 8, { gris: true });
  } else {
    campos([
      ['Trabajador', conMarca(c.trabajador, !!d.trabajadorEliminado)], ['Cédula', c.cedula], ['Fecha del examen', fechaCorta(c.fecha)],
      ['Tipo de examen', ETIQUETA_TIPO_EXAMEN[c.tipo_examen] ?? (c.tipo_examen || '—')], ['Aptitud', ETIQUETA_APTITUD[c.aptitud] ?? (c.aptitud || '—')],
      ['Médico', c.medico],
    ]);
    texto(d.certificadoMedico
      ? 'La firma del médico consta en el certificado adjunto al registro (documento firmado por el médico).'
      : 'Firma del médico: en el certificado firmado por el médico, que se adjunta al registro.', 8, { gris: true });
  }

  // Firmas de cierre (técnico, responsable, trabajador de EPP o aptitud)
  const cierre = firmasRequeridas(c).filter(r => !conLista(c) || r.rol === 'tecnico');
  if (c.tipo === 'aptitud_medica' && d.blanco) cierre.push({ rol: 'responsable', worker_id: null, nombre: '', obligatoria: true });
  seccion('Firmas');
  espacio(44);
  const anchoCol = (ANCHO - 6) / 2;
  const y0 = y + 1;
  cierre.forEach((r, k) => {
    const x = MARGEN + (k % 2) * (anchoCol + 6);
    const f = firmaDe(r.rol, r.worker_id, r.cedula);
    recuadro(x, y0, anchoCol, 22, f);
    const etiqueta = c.tipo === 'aptitud_medica' && r.rol === 'responsable' ? 'Médico (firma y sello)'
      : c.tipo === 'epp_entrega' && r.rol === 'trabajador' ? 'Trabajador (recibe)' : ETIQUETA_ROL[r.rol] + (r.obligatoria ? '' : ' (opcional)');
    doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5); doc.text(T(etiqueta), x, y0 + 26);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
    const quien = f ? `${conMarca(f.nombre, f.rol === 'trabajador' && f.worker_id === null)}${f.cedula ? ` · C.I. ${f.cedula}` : ''}` : r.nombre ? r.nombre : d.blanco ? 'Nombre y C.I.:' : 'Sin firma';
    doc.text(T(quien), x, y0 + 30);
    datosFirma(x, y0 + 34, f);
  });
  y = y0 + 40;

  // Integridad
  seccion('Integridad del registro');
  if (d.hash) texto(`Huella del contenido (SHA-256): ${d.hash}`, 7.5);
  if (d.blanco) {
    texto('Imprima este formato, recoja las firmas a mano y suba el escaneo firmado en StorSafe, en el mismo registro.', 8);
  } else {
    if (d.metodo) texto(`Método: ${ETIQUETA_METODO[d.metodo]}.`, 8);
    texto(d.firmadoEn ? `Registro cerrado el ${fechaHora(d.firmadoEn)} (hora del servidor).` : 'El registro todavía no está cerrado.', 8);
    if (d.reaperturas) texto(`El registro se reabrió ${d.reaperturas === 1 ? 'una vez' : `${d.reaperturas} veces`}; los motivos constan en el historial de cambios.`, 8, { gris: true });
  }
  texto(TEXTO_FIRMA_SIMPLE, 7.5, { gris: true });

  // Pie
  const pie = `${titulo} · ${fechaCorta(c.fecha)} · generado ${d.fecha.toLocaleDateString('es-EC')}`;
  const paginas = doc.getNumberOfPages();
  for (let p = 1; p <= paginas; p++) {
    doc.setPage(p);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7.5); doc.setTextColor(100, 116, 139);
    doc.text(T(pie), MARGEN, 290);
    doc.text(T(`Página ${p} de ${paginas}`), 210 - MARGEN, 290, { align: 'right' });
    doc.setTextColor(15, 23, 42);
  }
  return doc;
}
